import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Search, FileText, Mail, Briefcase } from "lucide-react";
import { fmtCurrency, fmtRelative, extractName } from "@/lib/format";

export default function SearchPage() {
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setBusy(true);
    try {
      const r = await api.get("/search", { params: { q } });
      setData(r.data);
    } finally { setBusy(false); }
  };

  return (
    <div className="p-8 max-w-[1400px]" data-testid="search-page">
      <PageHeader
        eyebrow="Global search"
        title="Search"
        subtitle="Search across emails, attachment text, and opportunities."
      />

      <form onSubmit={submit} className="flex gap-2 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <Input
            data-testid="global-search-input"
            placeholder="Try: northwind, RFP, sarah…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-11 pl-10 font-mono text-sm"
            autoFocus
          />
        </div>
        <Button type="submit" disabled={busy} data-testid="global-search-btn" className="h-11 bg-zinc-900 hover:bg-zinc-800">
          {busy ? "Searching…" : "Search"}
        </Button>
      </form>

      {!data && (
        <div className="text-sm text-muted-foreground">Enter a query to search across the pipeline.</div>
      )}

      {data && (
        <div className="grid lg:grid-cols-3 gap-4">
          <section className="card-surface" data-testid="search-emails">
            <div className="px-4 py-3 border-b border-border flex items-center gap-2">
              <Mail size={12} /><span className="overline">Emails · {data.emails.length}</span>
            </div>
            <ul className="divide-y divide-border">
              {data.emails.map((e) => (
                <li key={e.id} className="px-4 py-3 hover:bg-zinc-50">
                  <Link to={`/emails/${e.id}`}>
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="font-medium text-sm truncate">{e.subject}</div>
                      <StatusBadge status={e.status} />
                    </div>
                    <div className="font-mono text-[11px] text-muted-foreground truncate">
                      {extractName(e.from_addr)} · {fmtRelative(e.ingested_at)}
                      {e.category && <span className="ml-2 px-1 bg-zinc-100 rounded text-zinc-700 uppercase text-[9px]">{e.category}</span>}
                    </div>
                  </Link>
                </li>
              ))}
              {data.emails.length === 0 && <li className="px-4 py-6 text-center text-sm text-muted-foreground">No emails</li>}
            </ul>
          </section>

          <section className="card-surface" data-testid="search-attachments">
            <div className="px-4 py-3 border-b border-border flex items-center gap-2">
              <FileText size={12} /><span className="overline">Attachments · {data.attachments.length}</span>
            </div>
            <ul className="divide-y divide-border">
              {data.attachments.map((a) => (
                <li key={a.id} className="px-4 py-3 hover:bg-zinc-50">
                  <Link to={`/emails/${a.email_id}`}>
                    <div className="font-medium text-sm truncate">{a.filename}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">{a.mime_type}</div>
                    <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{a.snippet}</div>
                  </Link>
                </li>
              ))}
              {data.attachments.length === 0 && <li className="px-4 py-6 text-center text-sm text-muted-foreground">No files</li>}
            </ul>
          </section>

          <section className="card-surface" data-testid="search-opps">
            <div className="px-4 py-3 border-b border-border flex items-center gap-2">
              <Briefcase size={12} /><span className="overline">Opportunities · {data.opportunities.length}</span>
            </div>
            <ul className="divide-y divide-border">
              {data.opportunities.map((o) => (
                <li key={o.opportunity_id} className="px-4 py-3 hover:bg-zinc-50">
                  <Link to={`/opportunities/${o.opportunity_id}`}>
                    <div className="font-medium text-sm truncate">{o.name}</div>
                    <div className="font-mono text-[11px] text-muted-foreground">
                      {o.account} · {o.stage} · {fmtCurrency(o.amount, true)}
                    </div>
                  </Link>
                </li>
              ))}
              {data.opportunities.length === 0 && <li className="px-4 py-6 text-center text-sm text-muted-foreground">No opportunities</li>}
            </ul>
          </section>
        </div>
      )}
    </div>
  );
}
